import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { useState } from "react";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SavedPlacesProvider } from "./contexts/SavedPlacesContext";
import { ChatProvider } from "./contexts/ChatContext";
import { ToastProvider } from "./contexts/ToastContext";
import { config } from "./config";
import Layout from "./Layout";
import ChatUI from "./components/ChatUI";
import MapViewLite, { MarkerData } from "./components/MapViewLite";
import GetStarted from "./components/auth/GetStarted";
import AuthForm from "./components/auth/AuthForm";
import { EmailAuthForm } from "./components/auth/EmailAuthForm";
import { ForgotPassword } from "./components/auth/ForgotPassword";
import { ResetPassword } from "./components/auth/ResetPassword";
import { EmailVerification } from "./components/auth/EmailVerification";
import Profile from "./components/Profile";
import Explore from "./components/Explore";
import SavedItems from "./components/SavedItems";
import Services from "./components/Services";
import ServicesHub from "./components/ServicesHub";
import Featured from "./components/Featured";
import Plan from "./components/advancedTrip/Plan";
import TermsOfService from "./components/legal/TermsOfService";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import CookieConsent from "./components/CookieConsent";
import AdminPanel from "./components/AdminPanel";

// Protected Route Component - Now allows unregistered users (free tier)
// Individual components handle feature restrictions via usePermissions hook
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { authState } = useAuth();
  
  if (authState.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  // Allow both authenticated and unauthenticated users
  // Permission checks are handled by usePermissions hook in each component
  return <>{children}</>;
}

// Public Route Component (redirect to app/chat if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { authState } = useAuth();
  
  if (authState.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  return authState.isAuthenticated ? <Navigate to="/app/chat" replace /> : <>{children}</>;
}

function AppRoutes() {
  const [mapMarkers, setMapMarkers] = useState<MarkerData[]>([]);
  const [highlightedId, setHighlightedId] = useState<string | number | null>(null);
  const [hoveredId, setHoveredId] = useState<string | number | null>(null); // For hover highlighting (visual only)
  const [onMapMarkerClick, setOnMapMarkerClick] = useState<((id: string | number) => void) | undefined>(undefined);

  // Carousel state from Chat feature
  const [carouselState, setCarouselState] = useState({ activeCardIndex: 0, cardSwipeOffset: 0, isCardSwiping: false });
  const [carouselHandlers, setCarouselHandlers] = useState<any>(null);
  const [carouselData, setCarouselData] = useState<any[]>([]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><GetStarted /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthForm /></PublicRoute>} />
      <Route path="/email-auth" element={<PublicRoute><EmailAuthForm /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<EmailVerification />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Protected Routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout
              mapMarkers={mapMarkers}
              highlightedId={highlightedId}
              setHighlightedId={setHighlightedId}
              hoveredId={hoveredId}
              onMapMarkerClick={onMapMarkerClick}
              carouselState={carouselState}
              carouselHandlers={carouselHandlers}
              carouselData={carouselData}
            />
          </ProtectedRoute>
        }
      >
        {/* Routes inside the protected layout */}
        <Route path="chat" element={<ChatUI setMapMarkers={setMapMarkers} highlightedId={highlightedId} setHighlightedId={setHighlightedId} setHoveredId={setHoveredId} setOnMapMarkerClick={setOnMapMarkerClick} setCarouselState={setCarouselState} setCarouselHandlers={setCarouselHandlers} setCarouselData={setCarouselData} />} />
        <Route path="explore" element={<Explore />} />
        <Route path="services" element={<ServicesHub />} />
        <Route path="services/fare-validator" element={<Services />} />
        <Route path="saved" element={<SavedItems />} />
        <Route path="plan" element={<Plan />} />
        <Route path="featured" element={<Featured />} />
        <Route path="profile" element={<Profile />} />
        <Route path="map" element={<MapViewLite markers={mapMarkers} showEmptyState={false} />} />
        {/* Default route for protected area */}
        <Route index element={<Navigate to="chat" replace />} />
      </Route>
      
      {/* Admin Route - Outside protected layout */}
      <Route
        path="/adminlord"
        element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      
      {/* Redirect old routes to new structure */}
      <Route path="/chat" element={<Navigate to="/app/chat" replace />} />
      <Route path="/explore" element={<Navigate to="/app/explore" replace />} />
      <Route path="/services" element={<Navigate to="/app/services" replace />} />
      <Route path="/tips" element={<Navigate to="/app/services" replace />} /> {/* Redirect old tips to services */}
      <Route path="/saved" element={<Navigate to="/app/saved" replace />} />
      <Route path="/trips" element={<Navigate to="/app/plan" replace />} /> {/* Redirect old trips to new plan */}
      <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
      <Route path="/map" element={<Navigate to="/app/map" replace />} />
    </Routes>
  );
}

export default function App() {
  const googleClientId = config.googleClientId;
  
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <SavedPlacesProvider>
              <ChatProvider>
                <AppRoutes />
                <CookieConsent />
              </ChatProvider>
            </SavedPlacesProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
